import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouterHelper, useTranslation } from '@shared/core';
import { CardsValueProps, ValueProp, ValuePropKey } from './ValuePropsTopBar.types';
import { TRANSITION_INTERVAL } from './ValuePropsTopBar.constants';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useShopStorefrontContext } from '../ShopStorefrontProvider';
import { useCatalogLoggingContext } from '../../Catalog.logging';

export const useValuePropsTopBar = () => {
  const { trackValuePropTopBarClicked, trackValuePropClicked, trackValuePropDrawerPage } = useCatalogLoggingContext();
  const { t2 } = useTranslation('catalogRegistry');
  const valuePropsTopBar = useRef(t2('valuePropsTopBar')).current;
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [activeValuePropVariant, setActiveValuePropVariant] = useState<number>(0);

  const { currentStorefront, config } = useShopStorefrontContext();

  const routerHelper = useRouterHelper();

  const cardsValueProps = useMemo<CardsValueProps>(() => {
    const translationsByStorefront = valuePropsTopBar[currentStorefront];
    const configByStorefront = config.valuePropsTopBarData;
    return {
      shipping: { ...translationsByStorefront['shipping'], ...configByStorefront['shipping'] },
      completionDiscount: { ...translationsByStorefront['completionDiscount'], ...configByStorefront['completionDiscount'] },
      priceMatchPromise: { ...translationsByStorefront['priceMatchPromise'], ...configByStorefront['priceMatchPromise'] },
      easyReturns: { ...translationsByStorefront['easyReturns'], ...configByStorefront['easyReturns'] }
    };
  }, [valuePropsTopBar, currentStorefront, config]);

  const elementsAmount = useMemo(() => Object.keys(cardsValueProps).length, [cardsValueProps]);

  const isBabyRegistryEnabled = useFeatureValue('enableBabyRegistrySoftLaunchFeatures');

  const isMobileOrTablet = useIsMobileOrTablet();

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveValuePropVariant(prev => (prev + 1) % elementsAmount);
    }, TRANSITION_INTERVAL);
    return () => clearInterval(interval);
  }, [elementsAmount]);

  const fullGradientString = useMemo(() => {
    const gradientValues = Object.keys(cardsValueProps).map(key => cardsValueProps[key as ValuePropKey].background);
    gradientValues.push(gradientValues[0]);
    return gradientValues.join(',');
  }, [cardsValueProps]);

  const activeCard = cardsValueProps[config.valuePropsTopBarOrder[activeValuePropVariant]];

  const toggleDrawer = useCallback(() => {
    if (!isDrawerOpen && activeCard) {
      trackValuePropTopBarClicked(activeCard.title);
    }

    setIsDrawerOpen(!isDrawerOpen);
  }, [isDrawerOpen, activeCard, trackValuePropTopBarClicked]);

  const handleOnValuePropClick = useCallback(
    (valueProp: ValueProp) => {
      trackValuePropClicked({
        method: 'topBarDrawer',
        title: valueProp.title,
        target: valueProp.targetUrl,
        productCatalogItemPosition: null
      });
    },
    [trackValuePropClicked]
  );

  return {
    toggleDrawer,
    isDrawerOpen,
    activeValuePropVariant,
    cardsValueProps,
    fullGradientString,
    elementsAmount,
    handleOnValuePropClick,
    shouldShowStorefrontToggle: !isMobileOrTablet && isBabyRegistryEnabled && !routerHelper.isAdminRoute,
    trackValuePropDrawerPage
  };
};
